import React, { useState, useEffect, useCallback, memo } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { inputStyles } from './styles';
import { PROFILE_PLATFORMS } from './constants';

const CodingProfilesSection = memo(({ initialData, authAxios, setError, onUpdate }) => {
    const [profiles, setProfiles] = useState(initialData || []);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialData) setProfiles(initialData);
    }, [initialData]);

    const getProfileUsername = useCallback((platform) => {
        const profile = profiles.find(p => p.platform === platform);
        return profile?.username || '';
    }, [profiles]);

    const updateProfileUsername = useCallback((platform, username) => {
        setProfiles(prev => {
            const existing = prev.find(p => p.platform === platform);
            if (existing) {
                return prev.map(p => p.platform === platform ? { ...p, username } : p);
            }
            return [...prev, { id: Date.now(), platform, username }];
        });
    }, []);

    const handleSaveAll = async () => {
        setSaving(true);
        try {
            const savedEntries = await Promise.all(
                profiles.filter(p => p.username?.trim()).map(async profile => {
                    const payload = { platform: profile.platform, username: profile.username };
                    if (typeof profile.id === 'number' && profile.id > 1000000000000) {
                        return (await authAxios.post('/coding-profiles/', payload)).data;
                    }
                    return (await authAxios.patch(`/coding-profiles/${profile.id}/`, payload)).data;
                })
            );
            setProfiles(savedEntries);
            onUpdate?.(savedEntries);
        } catch (error) {
            setError?.('Failed to save coding profiles');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827' }}>Coding Profiles</Typography>
                <Button variant="contained" onClick={handleSaveAll} disabled={saving}
                    sx={{ bgcolor: '#4f46e5', color: 'white', textTransform: 'none', fontWeight: 500, borderRadius: '6px', px: 3, '&:hover': { bgcolor: '#4338ca' } }}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : 'Save All'}
                </Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {PROFILE_PLATFORMS.map(platform => (
                    <Box key={platform.id}>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '0.875rem' }}>
                            {platform.icon} {platform.name}
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={platform.placeholder}
                            value={getProfileUsername(platform.id)}
                            onChange={(e) => updateProfileUsername(platform.id, e.target.value)}
                            sx={inputStyles}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: '#9ca3af', fontSize: '0.875rem', whiteSpace: 'nowrap', mr: 0.5 }}>
                                        {platform.urlBase}
                                    </Typography>
                                ),
                            }}
                        />
                    </Box>
                ))}
            </Box>
        </Box>
    );
});

CodingProfilesSection.displayName = 'CodingProfilesSection';
export default CodingProfilesSection;
